<script lang="ts">
  import { onMount } from 'svelte';
  import { currentPage, initUISettings } from './stores/uiStore';
  import { t } from './lib/i18n';
  import { apiLogger, LogLevel } from './lib/api-logger';
  
  // Components
  import Navbar from './components/Navbar.svelte';
  
  // Pages
  import Dashboard from './pages/Dashboard.svelte';
  import TimeSeriesDetail from './pages/TimeSeriesDetail.svelte';
  import Settings from './pages/Settings.svelte';
  import AdminPanel from './pages/AdminPanel.svelte';
  
  // State variables
  let loading = true;
  let errorMessage = '';
  
  // Define a more specific type for page parameters
  interface PageParams {
    id?: string;
    [key: string]: string | undefined;
  }
  
  let pageParams: PageParams = {}; // For storing route parameters

  // Initialize application
  onMount(async () => {
    try {
      // Инициализируем настройки UI из localStorage
      initUISettings();
      
      // Initialize API logger
      apiLogger.loadFromLocalStorage();
      apiLogger.log(LogLevel.INFO, 'Application started', {
        version: '1.0.0',
        timestamp: new Date().toISOString()
      });
      
      // Остальная инициализация
      loading = false;
    } catch (error) {
      console.error('Failed to initialize application:', error);
      errorMessage = error instanceof Error ? error.message : 'Unknown error occurred during initialization';
      loading = false;
    }
  });

  // Function to handle page navigation
  function handleNavigate(page: string, params = {}) {
    currentPage.set(page);
    pageParams = params;
  }
</script>

<div class="app-container">
  <Navbar on:navigate={e => handleNavigate(e.detail.page, e.detail.params)} />
  
  <main class="container-fluid py-4">
    {#if loading}
      <div class="d-flex justify-content-center align-items-center" style="height: 50vh;">
        <div class="text-center">
          <div class="loading-spinner"></div>
          <p class="mt-3">{t('Loading application...')}</p>
        </div>
      </div>
    {:else if errorMessage}
      <div class="alert alert-danger mt-4" role="alert">
        <h4 class="alert-heading">{t('Application Error')}</h4>
        <p>{errorMessage}</p>
        <hr>
        <p class="mb-0">{t('Please try refreshing the page or contact support if the issue persists.')}</p>
      </div>
    {:else}
      <!-- Render the current page based on state -->
      {#if $currentPage === 'dashboard'}
        <Dashboard on:navigate={e => handleNavigate(e.detail.page, e.detail.params)} />
      {:else if $currentPage === 'timeseries' && pageParams.id}
        <TimeSeriesDetail id={pageParams.id} />
      {:else if $currentPage === 'settings'}
        <Settings />
      {:else if $currentPage === 'admin'}
        <AdminPanel />
      {:else}
        <!-- Default to dashboard -->
        <Dashboard on:navigate={e => handleNavigate(e.detail.page, e.detail.params)} />
      {/if}
    {/if}
  </main>
  
  <footer class="bg-light text-muted py-3 border-top">
    <div class="container-fluid">
      <div class="d-flex justify-content-between align-items-center">
        <p class="mb-0">{t('Time Series Analysis Platform © 2025')}</p>
        <p class="mb-0">{t('Version 1.0.0')}</p>
      </div>
    </div>
  </footer>
</div>

<style>
  .app-container {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }
  
  main {
    flex: 1;
  }
  
  :global(body) {
    background-color: #f8f9fa;
    transition: background-color 0.3s ease, color 0.3s ease;
  }
  
  /* Глобальные стили для светлых панелей и темных шрифтов */
  :global(.card) {
    background-color: #ffffff;
    border-color: #e9ecef;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    overflow: hidden;
  }
  
  :global(.card-header) {
    background-color: #f8f9fa;
    border-color: #e9ecef;
    font-weight: 600;
    color: #212529;
  }
  
  :global(.card-body) {
    color: #212529;
  }
  
  :global(.text) {
    color: #212529;
  }
  
  :global(h1), :global(h2), :global(h3), :global(h4), :global(h5), :global(h6) {
    color: #212529;
    font-weight: 600;
  }
  
  :global(.table) {
    color: #212529;
  }
  
  :global(.form-control), :global(.form-select) {
    background-color: #ffffff;
    border-color: #ced4da;
    color: #212529;
  }
  
  :global(.input-group-text) {
    background-color: #f8f9fa;
    border-color: #ced4da;
    color: #212529;
  }
  
  :global(.btn-primary) {
    background-color: #4361ee;
    border-color: #4361ee;
    color: #ffffff;
  }
  
  :global(.btn-primary:hover) {
    background-color: #3a56d4;
    border-color: #3a56d4;
  }
  
  :global(.table-hover tbody tr:hover) {
    background-color: rgba(67, 97, 238, 0.05);
  }
  
  :global(.nav-link.active) {
    color: #4361ee !important;
    font-weight: 600;
  }
  
  :global(.badge) {
    font-weight: 500;
  }
  
</style>
